import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import FormSection from '../../components/admin/FormSection';
import { LoadingState } from '../../components/admin/StateViews';
import MultiImageManager from '../../components/MultiImageManager';
import { Cause, CauseFormValues } from '../../types';
import { CauseService } from '../../services/CauseService';
import { AuditLogService } from '../../services/AuditLogService';
import { MediaUploadService } from '../../services/MediaUploadService';
import {
  donationServiceBaseUrl,
  getDisplayApiBaseUrl,
} from '../../services/apiClient';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';

type CauseFormErrors = Partial<Record<keyof CauseFormValues, string>>;

type NumericCauseField = {
  [K in keyof CauseFormValues]: CauseFormValues[K] extends number | '' ? K : never;
}[keyof CauseFormValues];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const isValidUrl = (value: string) => {
  if (!value) {
    return true;
  }

  // Reject `data:` URIs — image fields must reference S3, not embed base64.
  // Use the gallery uploader to push a file through /admin/uploads/image.
  if (/^data:/i.test(value.trim())) {
    return false;
  }

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const createInitialState = (cause?: Cause): CauseFormValues => ({
  title: cause?.title || '',
  description: cause?.description || '',
  goalAmount: cause?.goalAmount || '',
  images: cause?.images || [],
  externalProgramId: cause?.externalProgramId || '',
  rank: cause?.rank ?? '',
  slug: cause?.slug || '',
  shortDescription: cause?.shortDescription || '',
  category: cause?.category || '',
  tags: cause?.tags.join(', ') || '',
  status: cause?.status || 'draft',
  featured: cause?.featured || false,
  location: cause?.location || '',
  startDate: cause?.startDate || '',
  endDate: cause?.endDate || '',
  organizer: cause?.organizer || '',
  seoTitle: cause?.seoTitle || '',
  seoDescription: cause?.seoDescription || '',
  coverImageUrl: cause?.coverImageUrl || '',
  titleEn: cause?.titleEn || '',
  titleAr: cause?.titleAr || '',
  descriptionTitle: cause?.descriptionTitle || '',
  descriptionTitleEn: cause?.descriptionTitleEn || '',
  descriptionTitleAr: cause?.descriptionTitleAr || '',
  descriptionDetails: cause?.descriptionDetails || '',
  descriptionDetailsEn: cause?.descriptionDetailsEn || '',
  descriptionDetailsAr: cause?.descriptionDetailsAr || '',
  goalAmountUsd: cause?.goalAmountUsd ?? '',
  goalAmountEgp: cause?.goalAmountEgp ?? '',
  raisedAmountUsd: cause?.raisedAmountUsd ?? '',
  raisedAmountEgp: cause?.raisedAmountEgp ?? '',
  mealPriceUsd: cause?.mealPriceUsd ?? '',
  mealPriceEgp: cause?.mealPriceEgp ?? '',
  message: cause?.message || '',
  messageAr: cause?.messageAr || '',
  messageEn: cause?.messageEn || '',
  colorPrimary: cause?.colorPrimary || '',
  colorSecondary: cause?.colorSecondary || '',
  colorThird: cause?.colorThird || '',
  isZakatEligible: cause?.isZakatEligible || false,
  raisedPercentage: cause?.raisedPercentage ?? '',
  createdById: cause?.createdById || '',
  updatedById: cause?.updatedById || '',
  published: cause?.published || false,
  shareable: cause?.shareable || false,
  isSpecial: cause?.isSpecial || false,
  shareText: cause?.shareText || '',
  shareTextAr: cause?.shareTextAr || '',
  shareTextEn: cause?.shareTextEn || '',
  minAmountEgp: cause?.minAmountEgp ?? '',
  minAmountUsd: cause?.minAmountUsd ?? '',
  suggestedDonationAmountsEgp:
    cause?.suggestedDonationAmountsEgp?.join(', ') || '100, 250, 500, 1000',
  mealType: cause?.mealType || '',
  specialType: cause?.specialType || '',
  familiesHelped: cause?.familiesHelped ?? '',
  mealsDistributed: cause?.mealsDistributed ?? '',
  numberDonors: cause?.numberDonors ?? '',
  isItemization: cause?.isItemization || false,
  donationEmailMessage: cause?.donationEmailMessage || '',
  donationSmsMessage: cause?.donationSmsMessage || '',
  donationsEnabled: cause?.donationsEnabled ?? true,
  preferredPaymentChannel: cause?.preferredPaymentChannel || 'aps_native',
  apsEnvironment: cause?.apsEnvironment || '',
  apsCommand: cause?.apsCommand || '',
  apsPaymentOption: cause?.apsPaymentOption || '',
  createdAt: cause?.createdAt || '',
  updatedAt: cause?.updatedAt || '',
  deletedAt: cause?.deletedAt || '',
});

const validateForm = (values: CauseFormValues): CauseFormErrors => {
  const errors: CauseFormErrors = {};

  if (!values.externalProgramId.trim()) {
    errors.externalProgramId = 'Source ID is required.';
  }

  if (values.rank === '' || Number(values.rank) <= 0 || !Number.isInteger(Number(values.rank))) {
    errors.rank = 'Rank must be a positive whole number.';
  }

  if (!values.title.trim()) {
    errors.title = 'Title is required.';
  }

  if (!values.description.trim()) {
    errors.description = 'Description is required.';
  }

  if (!values.slug.trim()) {
    errors.slug = 'Slug is required.';
  } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(values.slug)) {
    errors.slug = 'Slug must use lowercase letters, numbers, and hyphens only.';
  }

  if (!values.goalAmount || Number(values.goalAmount) <= 0) {
    errors.goalAmount = 'Goal amount must be greater than zero.';
  }

  if (values.coverImageUrl && !isValidUrl(values.coverImageUrl)) {
    errors.coverImageUrl = 'Cover image must be a valid URL.';
  }

  if (values.images.some((image) => image.url && !isValidUrl(image.url))) {
    errors.images = 'All image URLs must be valid.';
  }

  const suggestedAmounts = values.suggestedDonationAmountsEgp
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const parsedSuggestedAmounts = suggestedAmounts.map(Number);
  if (
    suggestedAmounts.length < 1 ||
    suggestedAmounts.length > 6 ||
    parsedSuggestedAmounts.some(
      (value) => !Number.isFinite(value) || value <= 0
    ) ||
    new Set(parsedSuggestedAmounts).size !== parsedSuggestedAmounts.length
  ) {
    errors.suggestedDonationAmountsEgp =
      'Enter 1 to 6 unique positive amounts separated by commas.';
  }

  if (
    values.startDate &&
    values.endDate &&
    new Date(values.endDate) < new Date(values.startDate)
  ) {
    errors.endDate = 'End date must be after the start date.';
  }

  return errors;
};

const CauseFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const isEditMode = Boolean(id);
  const [initialCause, setInitialCause] = useState<Cause | null>(null);
  const [values, setValues] = useState<CauseFormValues>(createInitialState());
  const [errors, setErrors] = useState<CauseFormErrors>({});
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isSlugDirty, setIsSlugDirty] = useState(false);
  const coverUploadRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchCause = async () => {
        try {
          const data = await CauseService.getCauseById(id);
          setInitialCause(data);
          setValues(createInitialState(data));
        } catch (loadError) {
          setError('Failed to fetch cause details.');
          console.error(loadError);
        } finally {
          setLoading(false);
        }
      };
      fetchCause();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    if (!isSlugDirty && (!isEditMode || !values.slug.trim())) {
      setValues((current) => ({
        ...current,
        slug: slugify(current.title),
      }));
    }
  }, [isEditMode, isSlugDirty, values.slug, values.title]);

  const imageUrls = useMemo(() => values.images.map((image) => image.url), [values.images]);

  const updateField = <K extends keyof CauseFormValues>(
    field: K,
    value: CauseFormValues[K]
  ) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const updateNumericField = (field: NumericCauseField, rawValue: string) => {
    updateField(
      field,
      (rawValue === '' ? '' : Number(rawValue)) as CauseFormValues[NumericCauseField]
    );
  };

  const [coverUploading, setCoverUploading] = useState(false);

  const handleCoverUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setCoverUploading(true);
    setError(null);
    try {
      const result = await MediaUploadService.uploadImageFile(file, 'cause-cover');
      updateField('coverImageUrl', result.url);
    } catch (uploadError: any) {
      const serverMessage =
        uploadError?.response?.data?.error?.message ||
        uploadError?.response?.data?.message ||
        uploadError?.message ||
        'Upload failed.';
      setError(`Failed to upload cover image: ${serverMessage}`);
    } finally {
      setCoverUploading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationErrors = validateForm(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setError('Please resolve the highlighted fields before saving.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...values,
        tags: values.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      };

      const savedCause = isEditMode && id
        ? await CauseService.updateCause(id, payload)
        : await CauseService.createCause(payload);

      await AuditLogService.recordEntry({
        module: 'causes',
        action: isEditMode ? 'updated' : 'created',
        description: `${savedCause.title} was ${isEditMode ? 'updated' : 'created'}.`,
        actorEmail: user?.email || 'admin@local',
        targetId: savedCause.id,
        targetLabel: savedCause.title,
      });

      showToast(isEditMode ? 'Cause updated successfully.' : 'Cause created successfully.');
      navigate(`/causes/${savedCause.id}`);
    } catch (submitError: any) {
      const statusCode = submitError?.response?.status;
      const responseData = submitError?.response?.data;
      const responseMessage =
        typeof responseData?.error?.message === 'string'
          ? responseData.error.message
          : Array.isArray(responseData?.error?.details)
            ? responseData.error.details.join(' ')
            : typeof responseData?.error === 'string'
              ? responseData.error
              : null;
      const legacyResponseMessage =
        typeof responseData?.message === 'string'
          ? responseData.message
          : typeof responseData === 'string' && responseData.trim()
            ? responseData
            : null;
      const donationServiceDisplayUrl = getDisplayApiBaseUrl(
        donationServiceBaseUrl
      );
      const serverMessage =
        responseMessage ||
        legacyResponseMessage ||
        (statusCode === 413
          ? 'Uploaded media is too large. Please use a smaller image or fewer gallery files.'
          : null) ||
        (submitError?.message === 'Network Error'
          ? `Cannot reach the app backend at ${donationServiceDisplayUrl}. If you are running locally, make sure the NestJS backend is available on port 3000.`
          : null) ||
        submitError?.message ||
        null;
      setError(
        serverMessage ||
          (isEditMode ? 'Failed to update cause.' : 'Failed to create cause.')
      );
      console.error(submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading cause form…" />;
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 4 } }}>
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <FormSection
            title={isEditMode ? 'Cause details' : 'Create cause'}
            description="Manage the full program record here, including source ID, rank, multilingual titles, and publishing controls."
          >
            <Grid item xs={12} md={4}>
              <TextField
                label="Source program ID"
                fullWidth
                required
                value={values.externalProgramId}
                onChange={(event) => updateField('externalProgramId', event.target.value)}
                error={Boolean(errors.externalProgramId)}
                helperText={errors.externalProgramId || 'Must be unique.'}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Rank"
                type="number"
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
                value={values.rank}
                onChange={(event) => updateNumericField('rank', event.target.value)}
                error={Boolean(errors.rank)}
                helperText={errors.rank || 'Must be unique.'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title"
                fullWidth
                required
                value={values.title}
                onChange={(event) => updateField('title', event.target.value)}
                error={Boolean(errors.title)}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title (English)"
                fullWidth
                value={values.titleEn}
                onChange={(event) => updateField('titleEn', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title (Arabic)"
                fullWidth
                value={values.titleAr}
                onChange={(event) => updateField('titleAr', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Slug"
                fullWidth
                required
                value={values.slug}
                onChange={(event) => {
                  setIsSlugDirty(true);
                  updateField('slug', event.target.value);
                }}
                error={Boolean(errors.slug)}
                helperText={errors.slug || 'Used for clean links and future SEO.'}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label="Short description"
                fullWidth
                value={values.shortDescription}
                onChange={(event) =>
                  updateField('shortDescription', event.target.value)
                }
                helperText="Used in admin previews and future highlight modules."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={values.status}
                  label="Status"
                  onChange={(event) => {
                    const nextStatus = event.target.value as CauseFormValues['status'];
                    updateField('status', nextStatus);
                    updateField('published', nextStatus === 'published');
                  }}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.featured}
                    onChange={(event) =>
                      updateField('featured', event.target.checked)
                    }
                  />
                }
                label="Featured cause"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.published}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      updateField('published', checked);
                      updateField('status', checked ? 'published' : 'draft');
                    }}
                  />
                }
                label="Published"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.shareable}
                    onChange={(event) =>
                      updateField('shareable', event.target.checked)
                    }
                  />
                }
                label="Shareable"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isSpecial}
                    onChange={(event) =>
                      updateField('isSpecial', event.target.checked)
                    }
                  />
                }
                label="Special cause"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isZakatEligible}
                    onChange={(event) =>
                      updateField('isZakatEligible', event.target.checked)
                    }
                  />
                }
                label="Zakat eligible"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isItemization}
                    onChange={(event) =>
                      updateField('isItemization', event.target.checked)
                    }
                  />
                }
                label="Itemization enabled"
              />
            </Grid>
          </FormSection>

          <FormSection
            title="Fundraising content"
            description="Multilingual descriptions, category context, and impact-related metrics."
          >
            <Grid item xs={12}>
              <TextField
                label="Primary description"
                fullWidth
                required
                multiline
                rows={6}
                value={values.description}
                onChange={(event) => updateField('description', event.target.value)}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description title"
                fullWidth
                value={values.descriptionTitle}
                onChange={(event) => updateField('descriptionTitle', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description title (English)"
                fullWidth
                value={values.descriptionTitleEn}
                onChange={(event) =>
                  updateField('descriptionTitleEn', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description title (Arabic)"
                fullWidth
                value={values.descriptionTitleAr}
                onChange={(event) =>
                  updateField('descriptionTitleAr', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Category"
                fullWidth
                value={values.category}
                onChange={(event) => updateField('category', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Organizer"
                fullWidth
                value={values.organizer}
                onChange={(event) => updateField('organizer', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Location"
                fullWidth
                value={values.location}
                onChange={(event) => updateField('location', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Meal type"
                fullWidth
                value={values.mealType}
                onChange={(event) => updateField('mealType', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Special type"
                fullWidth
                value={values.specialType}
                onChange={(event) => updateField('specialType', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tags"
                fullWidth
                value={values.tags}
                onChange={(event) => updateField('tags', event.target.value)}
                helperText="Comma-separated tags."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description details"
                fullWidth
                multiline
                rows={5}
                value={values.descriptionDetails}
                onChange={(event) => updateField('descriptionDetails', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description details (English)"
                fullWidth
                multiline
                rows={5}
                value={values.descriptionDetailsEn}
                onChange={(event) => updateField('descriptionDetailsEn', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description details (Arabic)"
                fullWidth
                multiline
                rows={5}
                value={values.descriptionDetailsAr}
                onChange={(event) => updateField('descriptionDetailsAr', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Goal amount (primary)"
                type="number"
                fullWidth
                required
                value={values.goalAmount}
                onChange={(event) => updateNumericField('goalAmount', event.target.value)}
                error={Boolean(errors.goalAmount)}
                helperText={errors.goalAmount || 'Primary amount used by the donation service.'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Goal amount (USD)"
                type="number"
                fullWidth
                value={values.goalAmountUsd}
                onChange={(event) => updateNumericField('goalAmountUsd', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Goal amount (EGP)"
                type="number"
                fullWidth
                value={values.goalAmountEgp}
                onChange={(event) => updateNumericField('goalAmountEgp', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Raised amount (USD)"
                type="number"
                fullWidth
                value={values.raisedAmountUsd}
                onChange={(event) => updateNumericField('raisedAmountUsd', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Raised amount (EGP)"
                type="number"
                fullWidth
                value={values.raisedAmountEgp}
                onChange={(event) => updateNumericField('raisedAmountEgp', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Raised percentage"
                type="number"
                fullWidth
                value={values.raisedPercentage}
                onChange={(event) => updateNumericField('raisedPercentage', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Meal price (USD)"
                type="number"
                fullWidth
                value={values.mealPriceUsd}
                onChange={(event) => updateNumericField('mealPriceUsd', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Meal price (EGP)"
                type="number"
                fullWidth
                value={values.mealPriceEgp}
                onChange={(event) => updateNumericField('mealPriceEgp', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Minimum amount (USD)"
                type="number"
                fullWidth
                value={values.minAmountUsd}
                onChange={(event) => updateNumericField('minAmountUsd', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Minimum amount (EGP)"
                type="number"
                fullWidth
                value={values.minAmountEgp}
                onChange={(event) => updateNumericField('minAmountEgp', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Families helped"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                value={values.familiesHelped}
                onChange={(event) => updateNumericField('familiesHelped', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Meals distributed"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                value={values.mealsDistributed}
                onChange={(event) => updateNumericField('mealsDistributed', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Number of donors"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                value={values.numberDonors}
                onChange={(event) => updateNumericField('numberDonors', event.target.value)}
              />
            </Grid>
            {isEditMode ? (
              <Grid item xs={12} md={4}>
                <TextField
                  label="Raised amount (live sync)"
                  type="number"
                  fullWidth
                  value={initialCause?.currentAmount || 0}
                  InputProps={{ readOnly: true }}
                  helperText="Current amount synced from donations."
                />
              </Grid>
            ) : null}
          </FormSection>

          <FormSection
            title="Messaging & sharing"
            description="Donor-facing messages, share text, and communication templates."
          >
            <Grid item xs={12} md={4}>
              <TextField
                label="Message"
                fullWidth
                multiline
                rows={4}
                value={values.message}
                onChange={(event) => updateField('message', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Message (English)"
                fullWidth
                multiline
                rows={4}
                value={values.messageEn}
                onChange={(event) => updateField('messageEn', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Message (Arabic)"
                fullWidth
                multiline
                rows={4}
                value={values.messageAr}
                onChange={(event) => updateField('messageAr', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Share text"
                fullWidth
                multiline
                rows={4}
                value={values.shareText}
                onChange={(event) => updateField('shareText', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Share text (English)"
                fullWidth
                multiline
                rows={4}
                value={values.shareTextEn}
                onChange={(event) => updateField('shareTextEn', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Share text (Arabic)"
                fullWidth
                multiline
                rows={4}
                value={values.shareTextAr}
                onChange={(event) => updateField('shareTextAr', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Donation email message"
                fullWidth
                multiline
                rows={4}
                value={values.donationEmailMessage}
                onChange={(event) =>
                  updateField('donationEmailMessage', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Donation SMS message"
                fullWidth
                multiline
                rows={4}
                value={values.donationSmsMessage}
                onChange={(event) =>
                  updateField('donationSmsMessage', event.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Created by ID"
                fullWidth
                value={values.createdById}
                onChange={(event) => updateField('createdById', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Updated by ID"
                fullWidth
                value={values.updatedById}
                onChange={(event) => updateField('updatedById', event.target.value)}
              />
            </Grid>
          </FormSection>

          <FormSection
            title="Donations & APS"
            description="Control whether the cause accepts donations and which APS checkout settings the mobile app should use."
          >
            <Grid item xs={12} md={6}>
              <TextField
                label="Suggested donation amounts (EGP)"
                fullWidth
                value={values.suggestedDonationAmountsEgp}
                onChange={(event) =>
                  updateField('suggestedDonationAmountsEgp', event.target.value)
                }
                error={Boolean(errors.suggestedDonationAmountsEgp)}
                helperText={
                  errors.suggestedDonationAmountsEgp ||
                  'Comma-separated values shown as quick choices in the app, e.g. 100, 250, 500, 1000.'
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.donationsEnabled}
                    onChange={(event) =>
                      updateField('donationsEnabled', event.target.checked)
                    }
                  />
                }
                label="Donations enabled"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Payment channel</InputLabel>
                <Select
                  value={values.preferredPaymentChannel}
                  label="Payment channel"
                  onChange={(event) =>
                    updateField(
                      'preferredPaymentChannel',
                      event.target.value as CauseFormValues['preferredPaymentChannel']
                    )
                  }
                >
                  <MenuItem value="aps_native">APS mobile SDK</MenuItem>
                  <MenuItem value="hosted_webview">Hosted web payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>APS environment</InputLabel>
                <Select
                  value={values.apsEnvironment}
                  label="APS environment"
                  onChange={(event) =>
                    updateField(
                      'apsEnvironment',
                      event.target.value as CauseFormValues['apsEnvironment']
                    )
                  }
                >
                  <MenuItem value="">Backend default</MenuItem>
                  <MenuItem value="TEST">Sandbox (TEST)</MenuItem>
                  <MenuItem value="PRODUCTION">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>APS command</InputLabel>
                <Select
                  value={values.apsCommand}
                  label="APS command"
                  onChange={(event) =>
                    updateField(
                      'apsCommand',
                      event.target.value as CauseFormValues['apsCommand']
                    )
                  }
                >
                  <MenuItem value="">Backend default</MenuItem>
                  <MenuItem value="PURCHASE">PURCHASE</MenuItem>
                  <MenuItem value="AUTHORIZATION">AUTHORIZATION</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="APS payment option"
                fullWidth
                value={values.apsPaymentOption}
                onChange={(event) => updateField('apsPaymentOption', event.target.value)}
                helperText="Optional card brand or token option override."
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity={values.donationsEnabled ? 'info' : 'warning'}>
                {values.donationsEnabled
                  ? 'Mobile donation screens will read this cause-level payment setup before creating the APS intent.'
                  : 'This cause will stay visible, but donation attempts will be blocked until donations are enabled again.'}
              </Alert>
            </Grid>
          </FormSection>

          <FormSection
            title="Media & schedule"
            description="Cover media, gallery uploads, scheduling, and branding colors."
          >
            <Grid item xs={12}>
              <MultiImageManager
                imageUrls={imageUrls}
                onImageUrlsChange={(newUrls) =>
                  updateField(
                    'images',
                    newUrls.map((url, index) => ({
                      id: values.images[index]?.id || crypto.randomUUID(),
                      url,
                      isPrimary: index === 0,
                    }))
                  )
                }
                isReadOnly={false}
                helperText={
                  errors.images ||
                  'Reuse the same image URL pattern already used in the current admin forms.'
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.25}>
                <TextField
                  label="Cover image URL"
                  fullWidth
                  value={values.coverImageUrl}
                  onChange={(event) =>
                    updateField('coverImageUrl', event.target.value)
                  }
                  error={Boolean(errors.coverImageUrl)}
                  helperText={
                    errors.coverImageUrl ||
                    'Paste a URL or upload directly from your gallery.'
                  }
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={() => coverUploadRef.current?.click()}
                    disabled={coverUploading}
                  >
                    {coverUploading ? 'Uploading…' : 'Upload Cover From Gallery'}
                  </Button>
                  <input
                    ref={coverUploadRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleCoverUpload}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                value={values.startDate}
                onChange={(event) => updateField('startDate', event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="End date"
                type="date"
                fullWidth
                value={values.endDate}
                onChange={(event) => updateField('endDate', event.target.value)}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.endDate)}
                helperText={errors.endDate}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Primary color"
                fullWidth
                value={values.colorPrimary}
                onChange={(event) => updateField('colorPrimary', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Secondary color"
                fullWidth
                value={values.colorSecondary}
                onChange={(event) => updateField('colorSecondary', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Third color"
                fullWidth
                value={values.colorThird}
                onChange={(event) => updateField('colorThird', event.target.value)}
              />
            </Grid>
          </FormSection>

          <FormSection
            title="SEO metadata"
            description="Prepared fields for future search, social previews, and meta indexing."
          >
            <Grid item xs={12} md={6}>
              <TextField
                label="SEO title"
                fullWidth
                value={values.seoTitle}
                onChange={(event) => updateField('seoTitle', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="SEO description"
                fullWidth
                value={values.seoDescription}
                onChange={(event) =>
                  updateField('seoDescription', event.target.value)
                }
              />
            </Grid>
          </FormSection>

          {isEditMode ? (
            <FormSection
              title="Record timeline"
              description="System-managed timestamps stored with the cause."
            >
              <Grid item xs={12} md={4}>
                <TextField
                  label="Created at"
                  fullWidth
                  value={values.createdAt}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Updated at"
                  fullWidth
                  value={values.updatedAt}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Deleted at"
                  fullWidth
                  value={values.deletedAt}
                  InputProps={{ readOnly: true }}
                  helperText="Set automatically when the cause is archived."
                />
              </Grid>
            </FormSection>
          ) : null}

          <Stack
            direction="row"
            justifyContent={isEditMode && id ? 'space-between' : 'flex-end'}
            spacing={2}
          >
            {isEditMode && id ? (
              <Button
                variant="text"
                onClick={() => navigate(`/causes/${id}/tracking-config`)}
                disabled={submitting}
              >
                Tracking & Routing
              </Button>
            ) : null}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => navigate(isEditMode && id ? `/causes/${id}` : '/causes')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : isEditMode ? (
                  'Save Changes'
                ) : (
                  'Create Cause'
                )}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default CauseFormPage;
